/**
 * @fileoverview Calendar feed (ICS subscription) settings view.
 *
 * Rendered by client-container when the calendar view is "settings".
 * Self-contained: owns its own Convex queries/mutations. Lets the user
 * generate/copy/regenerate/revoke their personal feed URL and refine the
 * feed along three independent axes that AND together:
 *   Categories  →  Job status  →  Specific jobs
 * Each axis defaults to "all" (stored as null = dynamic, so newly added
 * jobs/categories auto-appear). At fetch time job filters are applied
 * against the user's current memberships.
 */

import { useEffect, useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { Copy, Check, RefreshCw, CalendarPlus } from "lucide-react";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { KeyDateCategory } from "@cvx/keyDates/jobKeyDates/schema";
import type { JobStatus } from "@cvx/jobs/schema";
import { JOB_STATUS_ORDER, JOB_STATUS_LABELS } from "@/lib/job-statuses";

import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { Checkbox } from "@/ui/primitives/checkbox";
import { Badge } from "@/ui/primitives/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/ui/primitives/card";

import { CATEGORY_META, CATEGORY_DISPLAY_ORDER } from "@/calendar/categoryMeta";
import { cn } from "@/utils/misc";

const ALL_CATEGORIES = CATEGORY_DISPLAY_ORDER;

export function CalendarFeedSettings() {
  const config = useQuery(api.keyDates.feed.getFeedConfig);
  const regenerate = useMutation(api.keyDates.feed.regenerateFeedToken);
  const revoke = useMutation(api.keyDates.feed.revokeFeedToken);
  const updateConfig = useMutation(api.keyDates.feed.updateFeedConfig);

  const feed = config?.feed ?? null;
  const availableJobs = config?.availableJobs ?? [];

  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  // Local selection state, seeded from the server whenever the feed URL
  // changes (initial load + regenerate). Edits update local state and
  // persist; the resulting refetch keeps the same URL so we don't clobber.
  const [selectedCats, setSelectedCats] = useState<Set<KeyDateCategory>>(
    new Set(ALL_CATEGORIES),
  );
  const [selectedStatuses, setSelectedStatuses] = useState<Set<JobStatus>>(
    new Set(JOB_STATUS_ORDER),
  );
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());

  const feedUrl = feed?.url ?? null;

  useEffect(() => {
    if (!feed) return;
    setSelectedCats(new Set(feed.categories ?? ALL_CATEGORIES));
    setSelectedStatuses(new Set(feed.jobStatuses ?? JOB_STATUS_ORDER));
    setSelectedJobs(
      feed.jobIds
        ? new Set(feed.jobIds.map((id) => id.toString()))
        : new Set(availableJobs.map((j) => j.id.toString())),
    );
    // Re-seed only when the feed identity (URL) changes, not on every
    // config update — otherwise reactive refetches would clobber edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `feed` and `availableJobs` get fresh identities on every reactive config refetch; re-running on them would clobber the user's in-progress selection edits
  }, [feedUrl]);

  const allJobsSelected =
    availableJobs.length > 0 &&
    selectedJobs.size === availableJobs.length &&
    availableJobs.every((j) => selectedJobs.has(j.id.toString()));
  const allStatuses = selectedStatuses.size >= JOB_STATUS_ORDER.length;
  const allCats = selectedCats.size >= ALL_CATEGORIES.length;

  const persist = (
    cats: Set<KeyDateCategory>,
    statuses: Set<JobStatus>,
    jobs: Set<string>,
  ) => {
    const categories =
      cats.size >= ALL_CATEGORIES.length ? null : Array.from(cats);
    const jobStatuses =
      statuses.size >= JOB_STATUS_ORDER.length ? null : Array.from(statuses);
    const everyJob =
      availableJobs.length > 0 &&
      jobs.size === availableJobs.length &&
      availableJobs.every((j) => jobs.has(j.id.toString()));
    const jobIds = everyJob ? null : (Array.from(jobs) as Array<Id<"jobs">>);
    void updateConfig({ jobIds, jobStatuses, categories });
  };

  const toggleCat = (cat: KeyDateCategory) => {
    const next = new Set(selectedCats);
    if (next.has(cat)) next.delete(cat);
    else next.add(cat);
    setSelectedCats(next);
    persist(next, selectedStatuses, selectedJobs);
  };

  const toggleStatus = (status: JobStatus) => {
    const next = new Set(selectedStatuses);
    if (next.has(status)) next.delete(status);
    else next.add(status);
    setSelectedStatuses(next);
    persist(selectedCats, next, selectedJobs);
  };

  const toggleJob = (jobId: string) => {
    const next = new Set(selectedJobs);
    if (next.has(jobId)) next.delete(jobId);
    else next.add(jobId);
    setSelectedJobs(next);
    persist(selectedCats, selectedStatuses, next);
  };

  const setAllCats = (on: boolean) => {
    const next = on ? new Set(ALL_CATEGORIES) : new Set<KeyDateCategory>();
    setSelectedCats(next);
    persist(next, selectedStatuses, selectedJobs);
  };
  const setAllStatuses = (on: boolean) => {
    const next = on ? new Set(JOB_STATUS_ORDER) : new Set<JobStatus>();
    setSelectedStatuses(next);
    persist(selectedCats, next, selectedJobs);
  };
  const setAllJobs = (on: boolean) => {
    const next = on
      ? new Set(availableJobs.map((j) => j.id.toString()))
      : new Set<string>();
    setSelectedJobs(next);
    persist(selectedCats, selectedStatuses, next);
  };

  const handleCopy = async () => {
    if (!feedUrl) return;
    await navigator.clipboard.writeText(feedUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const runBusy = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  // Loading.
  if (config === undefined) {
    return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  }

  // Empty state — no feed generated yet.
  if (!feedUrl) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 p-12 text-center">
        <CalendarPlus className="size-8 text-muted-foreground" />
        <div>
          <h2 className="text-base font-semibold">
            Sync Morgan to your calendar
          </h2>
          <p className="mx-auto max-w-md text-sm text-muted-foreground">
            Generate a private link, then add it to Google, Apple, or Outlook to
            see your key dates in your own calendar. It refreshes automatically.
          </p>
        </div>
        <Button
          type="button"
          className="gap-2"
          disabled={busy}
          onClick={() => runBusy(() => regenerate({}))}
        >
          <CalendarPlus className="size-4" />
          Generate calendar link
        </Button>
      </div>
    );
  }

  const summaryBadges = [
    allCats ? "All categories" : `${selectedCats.size} categories`,
    allStatuses ? "All statuses" : `${selectedStatuses.size} statuses`,
    allJobsSelected ? "All jobs" : `${selectedJobs.size} jobs`,
  ];

  const sectionHeader = (
    title: string,
    description: string,
    allOn: boolean,
    onToggleAll: (on: boolean) => void,
  ) => (
    <CardHeader className="flex-row items-start justify-between space-y-0 pb-3">
      <div className="space-y-1">
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-auto px-2 py-1 text-xs"
        onClick={() => onToggleAll(!allOn)}
      >
        {allOn ? "Clear" : "Select all"}
      </Button>
    </CardHeader>
  );

  return (
    <div className="space-y-5 p-5">
      {/* Feed link — title + badges row, full-width URL row, helper text. */}
      <div className="space-y-3 rounded-lg border bg-background p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold">Calendar subscription link</h2>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-muted-foreground">Feeding</span>
            {summaryBadges.map((label) => (
              <Badge key={label} variant="secondary" className="font-normal">
                {label}
              </Badge>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Input
            readOnly
            value={feedUrl}
            className="flex-1 font-mono text-xs"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleCopy}
            aria-label="Copy feed URL"
          >
            {copied ? (
              <Check className="size-4" />
            ) : (
              <Copy className="size-4" />
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2"
            disabled={busy}
            onClick={() => runBusy(() => regenerate({}))}
          >
            <RefreshCw className="size-4" />
            <span className="hidden sm:inline">Regenerate</span>
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => runBusy(() => revoke({}))}
          >
            Revoke
          </Button>
        </div>
        <div className="space-y-1 text-xs text-muted-foreground">
          <p>
            Private link — anyone with it can see your selected events. Changes
            apply on the next refresh (no need to re-subscribe).
          </p>
          <p>
            Add it via <span className="text-foreground/70">Google</span> (Other
            calendars → From URL),{" "}
            <span className="text-foreground/70">Apple</span> (File → New
            Calendar Subscription), or{" "}
            <span className="text-foreground/70">Outlook</span> (Add calendar →
            Subscribe from web).
          </p>
        </div>
      </div>

      {/* 1. Categories */}
      <Card className="bg-background">
        {sectionHeader(
          "Categories",
          "Which kinds of key dates to include.",
          allCats,
          setAllCats,
        )}
        <CardContent>
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {ALL_CATEGORIES.map((cat) => {
              const meta = CATEGORY_META[cat];
              const Icon = meta.icon;
              return (
                <li key={cat} className="flex items-center gap-2">
                  <Checkbox
                    id={`cat-${cat}`}
                    checked={selectedCats.has(cat)}
                    onCheckedChange={() => toggleCat(cat)}
                  />
                  <Label
                    htmlFor={`cat-${cat}`}
                    className="flex cursor-pointer items-center gap-1.5 text-sm font-medium leading-none"
                  >
                    <span
                      className={cn(
                        "inline-flex size-5 shrink-0 items-center justify-center rounded",
                        meta.iconBgClass,
                      )}
                    >
                      <Icon className={cn("size-3", meta.iconFgClass)} />
                    </span>
                    {meta.label}
                  </Label>
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>

      {/* 2. Job status */}
      <Card className="bg-background">
        {sectionHeader(
          "Job status",
          "Only include jobs at these statuses (updates as jobs change status).",
          allStatuses,
          setAllStatuses,
        )}
        <CardContent>
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {JOB_STATUS_ORDER.map((status) => (
              <li key={status} className="flex items-center gap-2">
                <Checkbox
                  id={`status-${status}`}
                  checked={selectedStatuses.has(status)}
                  onCheckedChange={() => toggleStatus(status)}
                />
                <Label
                  htmlFor={`status-${status}`}
                  className="cursor-pointer text-sm font-normal"
                >
                  {JOB_STATUS_LABELS[status]}
                </Label>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* 3. Specific jobs */}
      <Card className="bg-background">
        {sectionHeader(
          "Jobs",
          "Narrow to specific jobs (leave all ticked to include every job).",
          allJobsSelected,
          setAllJobs,
        )}
        <CardContent>
          {availableJobs.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              You aren&apos;t a member of any jobs yet.
            </p>
          ) : (
            <ul className="grid max-h-72 grid-cols-1 gap-3 overflow-y-auto sm:grid-cols-2 lg:grid-cols-3">
              {availableJobs.map((job) => {
                const id = job.id.toString();
                return (
                  <li key={id} className="flex items-center gap-2">
                    <Checkbox
                      id={`job-${id}`}
                      checked={selectedJobs.has(id)}
                      onCheckedChange={() => toggleJob(id)}
                    />
                    <Label
                      htmlFor={`job-${id}`}
                      className="cursor-pointer truncate text-sm font-normal"
                    >
                      {job.name}
                    </Label>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
